/**
 * Crypto formatting utilities
 * Shared functions for formatting and displaying crypto balances
 */

/**
 * Get wallet key from currency and network
 * Used to uniquely identify wallet configurations
 *
 * @param currency - Crypto currency (BTC, ETH, USDT, etc.)
 * @param network - Optional network identifier (ERC20, TRC20, etc.)
 * @returns Wallet key string (e.g., "USDT-ERC20")
 *
 * @example
 * getWalletKey('USDT', 'ERC20') // Returns "USDT-ERC20"
 * getWalletKey('BTC', null) // Returns "BTC"
 */
export const getWalletKey = (
  currency: string,
  network?: string | null,
): string => {
  if (network) {
    return `${currency} (${network})`;
  }
  return currency;
};

/**
 * Format crypto balance for display
 * Converts balance to fixed decimal places
 *
 * @param amount - Balance amount (string or number)
 * @param decimals - Number of decimal places to show
 * @returns Formatted balance string
 *
 * @example
 * formatCryptoBalance(0.12345678, 8) // Returns "0.12345678"
 * formatCryptoBalance("1234.5", 2) // Returns "1234.50"
 */
export const formatCryptoBalance = (
  amount: string | number,
  decimals: number,
): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return num.toFixed(decimals);
};

/**
 * Get USD balance for display
 * USDT/USDC are 1:1 with USD, others use provided price or return '-'
 *
 * @param currency - Crypto currency
 * @param amount - Balance amount
 * @param price - Optional price per unit in USD (from crypto price API)
 * @returns USD value string or '-'
 *
 * @example
 * getUsdBalance('USDT', 1234.56) // Returns "1234.56"
 * getUsdBalance('BTC', 0.5, 45000) // Returns "22500.00"
 * getUsdBalance('BTC', 0.5) // Returns "-" (no price provided)
 */
export const getUsdBalance = (
  currency: string,
  amount: string | number,
  price?: number,
): string => {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Stablecoins are always 1:1 with USD
  if (currency === 'USDT' || currency === 'USDC') {
    return num.toFixed(2);
  }

  // If price is provided, calculate USD value
  if (price && price > 0) {
    const usdValue = num * price;
    return usdValue.toFixed(2);
  }

  // No price available
  return '-';
};

/**
 * Parse decimal amount to number
 * Safely converts Prisma Decimal or string to number
 *
 * @param amount - Decimal amount (string or number)
 * @returns Parsed number value
 *
 * @example
 * parseDecimalAmount("123.456") // Returns 123.456
 * parseDecimalAmount(123.456) // Returns 123.456
 */
export const parseDecimalAmount = (amount: string | number): number => {
  if (typeof amount === 'number') return amount;
  return parseFloat(amount);
};

/**
 * Format large numbers with abbreviations
 * Converts large numbers to K, M, B notation
 *
 * @param num - Number to format
 * @param decimals - Decimal places (default: 2)
 * @returns Formatted string with abbreviation
 *
 * @example
 * formatLargeNumber(1234) // Returns "1.23K"
 * formatLargeNumber(1234567) // Returns "1.23M"
 * formatLargeNumber(1234567890) // Returns "1.23B"
 */
export const formatLargeNumber = (
  num: number,
  decimals: number = 2,
): string => {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(decimals) + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(decimals) + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(decimals) + 'K';
  }
  return num.toFixed(decimals);
};
